import { Injectable, signal } from '@angular/core';

export interface ToasterConfig {
    id?: string;
    message: string;
    status: 'success' | 'error' | 'warning' | 'info' | 'copy';
    duration?: number;
    closable?: boolean;
    action?: {
        label: string;
        handler: () => void;
    };
}

export interface Toast extends ToasterConfig {
    id: string;
    timestamp: number;
}

@Injectable({
    providedIn: 'root',
})
export class ToasterService {
    private readonly toastsSignal = signal<Toast[]>([]);
    private toastIdCounter = 0;

    get toasts() {
        return this.toastsSignal;
    }

    show(config: ToasterConfig): string {
        const id = config.id || `toast-${++this.toastIdCounter}`;
        const toast: Toast = {
            ...config,
            id,
            timestamp: Date.now(),
            closable: config.closable ?? true,
        };

        // Add to toasts array
        this.toastsSignal.update((toasts) => [...toasts, toast]);

        // Auto hide after duration (unless duration is 0 for persistent toasts)
        const duration = config.duration ?? 5000;
        if (duration > 0) {
            setTimeout(() => {
                this.hide(id);
            }, duration);
        }

        return id;
    }

    hide(id: string) {
        this.toastsSignal.update((toasts) => toasts.filter((toast) => toast.id !== id));
    }

    hideAll() {
        this.toastsSignal.set([]);
    }

    // Convenience methods for different toast types
    success(message: string, options?: Partial<ToasterConfig>): string {
        return this.show({ ...options, message, status: 'success' });
    }

    error(message: string, options?: Partial<ToasterConfig>): string {
        return this.show({ ...options, message, status: 'error', duration: options?.duration ?? 7000 });
    }

    warning(message: string, options?: Partial<ToasterConfig>): string {
        return this.show({ ...options, message, status: 'warning', duration: options?.duration ?? 6000 });
    }

    info(message: string, options?: Partial<ToasterConfig>): string {
        return this.show({ ...options, message, status: 'info' });
    }

    copy(message: string = 'Copied to clipboard', options?: Partial<ToasterConfig>): string {
        return this.show({ ...options, message, status: 'copy', duration: options?.duration ?? 3000 });
    }
}
